import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { IonIcon } from '@ionic/react';
import {
  checkmarkCircleOutline,
  alertCircleOutline,
  informationCircleOutline,
  closeOutline,
} from 'ionicons/icons';
import type { NotificationKind } from '../../store/useNotificationStore';

// Upward travel (px) past which a touch drag dismisses the toast. Mirrors the
// activity banner's swipe-to-dismiss.
const SWIPE_DISMISS_THRESHOLD = 40;

// Per-kind styling: a semantic brand background (green / red / brand) plus the
// matching leading icon. brand-* utilities map to Ionic's --ion-color-* tokens
// (see utils.scss), so they're theme-aware and their contrast is white in both
// light and dark — white text/tints read correctly on all three.
const KIND_STYLES: Record<NotificationKind, { bg: string; icon: string }> = {
  success: { bg: 'bg-brand-success', icon: checkmarkCircleOutline },
  error: { bg: 'bg-brand-danger', icon: alertCircleOutline },
  info: { bg: 'bg-brand-primary', icon: informationCircleOutline },
};

/** An inline action rendered inside a toast (e.g. Undo). Tapping it runs
 *  `onClick` and dismisses the toast — the toast IS the action's whole
 *  lifetime, so leaving it up after the action fired would invite a second tap
 *  on an operation that has already happened. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface MessageToastBannerProps {
  kind: NotificationKind;
  /** Message body. May contain newlines (detail lines) — rendered as-is. */
  message: string;
  id: string;
  /** Optional inline action button (e.g. Undo). */
  action?: ToastAction;
  /** Finite ms → auto-dismisses and shows a countdown bar. Infinity → sticky
   * (no timer, no bar) but still closable via the X or a swipe. */
  duration: number;
  visible: boolean;
  /** react-hot-toast's `createdAt`, which bumps on every in-place update (same
   * id). Restarts both the countdown bar and the auto-dismiss timer so an
   * updated toast gets its full duration again. */
  timerKey?: number;
}

/**
 * Styled text toast — the branded counterpart to the plain react-hot-toast
 * pills. Semantic color per kind, a close button, and a self-managed
 * auto-dismiss timer immune to react-hot-toast's stuck pause-on-hover bug (see
 * the timer note below). This is what every `notify.*` call renders.
 */
const MessageToastBanner: React.FC<MessageToastBannerProps> = ({
  kind,
  message,
  id,
  action,
  duration,
  visible,
  timerKey,
}) => {
  const touchStartY = useRef<number | null>(null);
  const swiped = useRef(false);
  const dismiss = () => toast.dismiss(id);

  // Self-manage auto-dismiss (react-hot-toast's own duration is Infinity for
  // these). Its pause-on-hover can get stuck on touch — a stray mouseenter over
  // the toaster with no mouseleave pauses every timer, so the toast never
  // leaves. A plain timer can't get stuck. Resets with `timerKey` so an in-place
  // replace (same id) restarts it alongside the bar.
  useEffect(() => {
    if (!Number.isFinite(duration)) return;
    const t = setTimeout(() => toast.dismiss(id), duration);
    return () => clearTimeout(t);
  }, [id, duration, timerKey]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    swiped.current = false;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null || swiped.current) return;
    if (touchStartY.current - e.touches[0].clientY > SWIPE_DISMISS_THRESHOLD) {
      swiped.current = true;
      dismiss();
    }
  };

  const { bg, icon } = KIND_STYLES[kind];

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      className={[
        // relative/overflow-hidden so the countdown bar pins to the bottom edge
        // and clips to the rounded corners. pr for the absolute close button.
        'relative flex w-full max-w-sm items-start gap-2.5 overflow-hidden rounded-xl border border-white/20 p-3 pr-8 text-left text-white shadow-[0_18px_40px_-8px_rgba(0,0,0,0.55)]',
        bg,
        // Slide down on enter, back up on leave — same motion as the activity
        // banner. fill-mode-forwards keeps the toast slid-out until react-hot-toast
        // unmounts it, so the exit doesn't snap back to a second "enter".
        visible
          ? 'animate-in fade-in slide-in-from-top-16 duration-300 ease-out'
          : 'animate-out fade-out slide-out-to-top-16 fill-mode-forwards duration-200 ease-in',
      ].join(' ')}
    >
      <IonIcon
        icon={icon}
        aria-hidden="true"
        className="mt-0.5 flex-shrink-0 text-lg text-white"
      />
      {/* whitespace-pre-line renders the detail newlines injected by notify.ts. */}
      <span className="min-w-0 flex-1 whitespace-pre-line break-words text-sm font-medium">
        {message}
      </span>

      {/* Action (e.g. Undo). White-on-tint rather than a brand color: the banner
          background is already a brand fill, so a colored button on it would be
          the one unreadable element. Sits before the close button in the DOM so
          it's the first thing reached by keyboard/screen reader. */}
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            dismiss();
          }}
          className="mt-px flex-shrink-0 rounded-lg bg-white/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-white/30 active:opacity-80"
        >
          {action.label}
        </button>
      )}

      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white"
      >
        <IonIcon icon={closeOutline} className="text-base" />
      </button>

      {/* Countdown bar — only for auto-dismissing (finite) toasts. Sticky toasts
          (Infinity) show none. Length matches `duration` so it empties as the
          toast begins to dismiss. */}
      {Number.isFinite(duration) && (
        <span
          key={timerKey}
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1 origin-left bg-white/40"
          style={{ animation: `hp-toast-timer ${duration}ms linear forwards` }}
        />
      )}
    </div>
  );
};

interface ShowMessageToastArgs {
  kind: NotificationKind;
  message: string;
  id: string;
  /** ms, or null for a sticky toast that stays until manually dismissed. */
  duration: number | null;
  action?: ToastAction;
}

/**
 * Render (or replace, by id) a branded message toast. Called by the `notify`
 * service in place of react-hot-toast's default `toast.success/error()` pills.
 */
export function showMessageToast({
  kind,
  message,
  id,
  duration,
  action,
}: ShowMessageToastArgs): void {
  const effectiveDuration = duration === null ? Infinity : duration;
  toast.custom(
    (tst) => (
      <MessageToastBanner
        kind={kind}
        message={message}
        id={id}
        action={action}
        duration={effectiveDuration}
        visible={tst.visible}
        timerKey={tst.createdAt}
      />
    ),
    // Infinity: react-hot-toast never auto-dismisses these — the banner's own
    // timer (synced to the bar) owns dismissal, immune to stuck pause-on-hover.
    { id, duration: Infinity },
  );
}
